import React from 'react';
import { ICellRendererParams } from '@ag-grid-enterprise/all-modules';
import styled from 'styled-components';
import { IConceptLinkerValidatorItem } from 'types/conceptLinkerValidator';
import ConceptCardHover from 'components/common/ConceptCardHover';

const ResultContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 1px 0;
`;

const DecisionContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const DecisionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: nowrap;
`;

const DecisionText = styled.span<{ $color: string }>`
  color: ${(props) => props.$color};
  font-weight: 600;
  white-space: nowrap;
`;

const ConfidenceBadge = styled.span<{ $confidence: number }>`
  display: inline-flex;
  align-items: center;
  padding: 0px 5px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  background-color: ${(props) => {
    const conf = props.$confidence;
    if (conf >= 0.8) return '#e8f5e9';
    if (conf >= 0.6) return '#fff3e0';
    return '#ffebee';
  }};
  color: ${(props) => {
    const conf = props.$confidence;
    if (conf >= 0.8) return '#2e7d32';
    if (conf >= 0.6) return '#ef6c00';
    return '#c62828';
  }};
  border: 1px solid ${(props) => {
    const conf = props.$confidence;
    if (conf >= 0.8) return '#a5d6a7';
    if (conf >= 0.6) return '#ffcc80';
    return '#ef9a9a';
  }};
`;

const ReasoningText = styled.div`
  color: #666;
  line-height: 1.3;
  white-space: normal;
  cursor: help;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UnlinkableSection = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
`;

const UnlinkableLabel = styled.span`
  color: #d32f2f;
  font-weight: 600;
  white-space: nowrap;
`;

const UnlinkableConceptsList = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0;
`;

const ConceptValidatorAiResultRenderer: React.FC<ICellRendererParams> = (params) => {
  const data = params.data as IConceptLinkerValidatorItem;

  if (!data || !data.ai_result || !data.ai_result.decision) {
    return null;
  }

  const { decision, confidence, reasoning, selected_ref, selected_refId, unlinkable_concepts } = data.ai_result;

  // Determine decision type
  const isKeep = decision === 'keep';
  const isChange = decision === 'change';
  const isUnlink = decision === 'unlink';
  const isError = decision === 'error';
  const hasAlternative = selected_ref !== undefined && selected_refId !== undefined;

  // Determine decision color
  const decisionColor = isKeep ? '#4caf50' : isError ? '#ff9800' : '#f44336';

  // Create decision text/component for different decisions
  let decisionContent: React.ReactNode;
  if (isKeep) {
    decisionContent = <DecisionText $color={decisionColor}>Keep</DecisionText>;
  } else if (isChange && hasAlternative && selected_ref !== undefined && selected_refId !== undefined) {
    decisionContent = (
      <>
        <DecisionText $color={decisionColor}>Change to</DecisionText>
        <ConceptCardHover conceptRef={selected_ref} refId={selected_refId} courseId={data.courseId} showLabel={true} />
      </>
    );
  } else if (isUnlink) {
    decisionContent = <DecisionText $color={decisionColor}>Unlink</DecisionText>;
  } else if (isError) {
    decisionContent = <DecisionText $color={decisionColor}>Error</DecisionText>;
  } else {
    decisionContent = <DecisionText $color={decisionColor}>Change</DecisionText>; // Fallback if no alternative specified
  }

  // Format confidence as percentage
  const confidencePercent = confidence ? `${Math.round(confidence * 100)}%` : null;

  // Show reasoning for non-keep decisions (change, unlink, error)
  const shouldShowReasoning = !isKeep && reasoning;
  const truncatedReasoning =
    shouldShowReasoning && reasoning
      ? reasoning.length > 150
        ? reasoning.substring(0, 150) + '...'
        : reasoning
      : null;

  // Format unlinkable concepts if any
  const hasUnlinkableConcepts = unlinkable_concepts && unlinkable_concepts.length > 0;

  return (
    <ResultContainer>
      <DecisionContainer>
        <DecisionHeader>
          {decisionContent}
          {confidence && <ConfidenceBadge $confidence={confidence}>{confidencePercent}</ConfidenceBadge>}
        </DecisionHeader>
      </DecisionContainer>

      {truncatedReasoning && reasoning && <ReasoningText title={reasoning}>{truncatedReasoning}</ReasoningText>}

      {hasUnlinkableConcepts && (
        <UnlinkableSection>
          <UnlinkableLabel>⚠️ Cannot link:</UnlinkableLabel>
          <UnlinkableConceptsList>
            {unlinkable_concepts.map((concept: any, index: number) => (
              <React.Fragment key={`${concept.ref}_${concept.refId || concept.ref_id}`}>
                {index > 0 && <span style={{ color: '#666', margin: '0 2px' }}>,</span>}
                <ConceptCardHover
                  conceptRef={concept.ref}
                  refId={concept.refId || concept.ref_id}
                  courseId={data.courseId}
                  showLabel={true}
                />
              </React.Fragment>
            ))}
          </UnlinkableConceptsList>
        </UnlinkableSection>
      )}
    </ResultContainer>
  );
};

export default ConceptValidatorAiResultRenderer;
